import "./App.css";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Home as HomeIcon, 
  ChefHat, 
  LeafyGreen, 
  Sparkles, 
  Heart,
  Instagram,
  Menu,
  X,
  UtensilsCrossed
} from "lucide-react";
import { useState, useEffect } from "react";

// Brand Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_homemade-chutney-1/artifacts/07funu8v_WhatsApp%20Image%202026-03-27%20at%208.45.02%20PM.jpeg";
const HERO_BG = "https://images.pexels.com/photos/2632292/pexels-photo-2632292.jpeg";
const STORY_IMAGE = "https://images.pexels.com/photos/4871289/pexels-photo-4871289.jpeg";
const INSTAGRAM_LINK = "https://www.instagram.com/ammaruchi.official?igsh=MTkzbWR5eDZ6OXh5Yw==";

// Product Images
const PRODUCTS = [
  {
    id: "chutney-powder",
    name: "Idli Podi",
    description: "Our signature chutney powder made with roasted lentils, dried chilies, and a secret blend of spices. Perfect with steaming hot idlis and a drizzle of sesame oil.",
    image: "https://images.unsplash.com/photo-1580855427191-7c8257ceec7c"
  },
  {
    id: "pickle",
    name: "Avakai Pickle",
    description: "Traditional Andhra-style raw mango pickle, sun-dried and slow-cured with mustard, fenugreek, and red chili. Just like Amma used to make.",
    image: "https://images.pexels.com/photos/5855875/pexels-photo-5855875.jpeg"
  },
  {
    id: "spice-mix",
    name: "Sambar Masala",
    description: "A fragrant spice blend of coriander, cumin, and curry leaves roasted to perfection. Transforms your everyday sambar into something extraordinary.",
    image: "https://images.unsplash.com/photo-1771541897176-44a3e01dc484"
  }
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#home", label: "Home" },
    { href: "#story", label: "Story" },
    { href: "#products", label: "Products" },
    { href: "#contact", label: "Contact" }
  ];

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-[#FDF6EC]/95 backdrop-blur-xl shadow-sm border-b border-[#E5D5C1]" 
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center" data-testid="nav-logo">
            <img 
              src={LOGO_URL} 
              alt="Amma Ruchi" 
              className="h-14 w-auto object-contain rounded-2xl"
            />
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className="font-medium text-[#2C1E16] hover:text-[#E8651A] transition-colors duration-200"
              >
                {link.label}
              </a>
            ))}
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            data-testid="mobile-menu-button"
            className="md:hidden p-2 text-[#2C1E16]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        
        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#FDF6EC]/95 backdrop-blur-xl border-t border-[#E5D5C1]"
            data-testid="mobile-menu"
          >
            <div className="py-4 space-y-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  className="block px-4 py-3 font-medium text-[#2C1E16] hover:text-[#E8651A] hover:bg-[#F5EBDB] rounded-lg transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="home" 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A0F0A]/80 via-[#2C1E16]/60 to-[#FDF6EC]" />
      
      {/* Content */}
      <div className="relative z-10 text-center px-6 pt-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          <img 
            src={LOGO_URL} 
            alt="Amma Ruchi - Every Bite Feels Like Home" 
            className="h-32 sm:h-40 lg:h-48 mx-auto object-contain drop-shadow-2xl rounded-3xl"
            data-testid="hero-logo"
          />
        </motion.div>
        
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-xl sm:text-2xl lg:text-3xl text-white/90 font-light mb-12 max-w-2xl mx-auto"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
          data-testid="hero-tagline"
        >
          Every Bite Feels Like Home
        </motion.p>
        
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          whileHover={{ scale: 1.05, y: -4 }}
          whileTap={{ scale: 0.98 }}
          data-testid="hero-cta"
          className="bg-[#E8651A] text-white rounded-full px-10 py-4 text-lg font-medium 
                     hover:bg-[#D75A12] transition-all duration-300 shadow-lg shadow-[#E8651A]/30"
        >
          Orders Coming Soon
        </motion.button>
      </div>
      
      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-[#E8651A]/50 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-[#E8651A] rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};

// Story Section
const StorySection = () => {
  return (
    <section id="story" data-testid="story-section" className="py-24 sm:py-32 lg:py-40 bg-[#FDF6EC]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <AnimatedSection>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl">
                <img 
                  src={STORY_IMAGE} 
                  alt="Traditional spice preparation" 
                  className="w-full h-full object-cover"
                  data-testid="story-image"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#F5A623]/20 rounded-full -z-10" />
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-[#2D6A2D]/10 rounded-full -z-10" />
            </div>
            
            {/* Content */}
            <div>
              <h2 
                className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2C1E16] mb-8"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
                data-testid="story-heading"
              >
                Our Story
              </h2>
              <p 
                className="text-lg sm:text-xl text-[#2C1E16]/80 leading-relaxed mb-6"
                data-testid="story-text"
              >
                In every South Indian home, there's a grandmother whose chutney powder was legendary. 
                Amma Ruchi was born from those cherished recipes, passed down through three generations 
                of our family.
              </p>
              <p className="text-lg sm:text-xl text-[#2C1E16]/80 leading-relaxed">
                We still roast our spices the old way — in cast iron, over wood fire, with patience 
                and love. Because some traditions deserve to be preserved, one small batch at a time.
              </p>
              
              {/* Decorative divider */}
              <div className="flex items-center gap-4 mt-10">
                <div className="h-px flex-1 bg-gradient-to-r from-[#E8651A] to-transparent" />
                <LeafyGreen className="text-[#2D6A2D]" size={24} />
                <div className="h-px flex-1 bg-gradient-to-l from-[#2D6A2D] to-transparent" />
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Products Section
const ProductsSection = () => {
  return (
    <section id="products" data-testid="products-section" className="py-24 sm:py-32 lg:py-40 bg-[#F5EBDB]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <AnimatedSection className="text-center mb-16">
          <h2 
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2C1E16] mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="products-heading"
          >
            Our Products
          </h2>
          <p className="text-lg text-[#2C1E16]/70 max-w-2xl mx-auto">
            Handcrafted in small batches with recipes that have stood the test of time
          </p>
        </AnimatedSection>
        
        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {PRODUCTS.map((product, index) => (
            <motion.div
              key={product.id}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="group"
              data-testid={`product-card-${product.id}`}
            >
              <div className="bg-[#FDF6EC] rounded-3xl overflow-hidden border border-[#E5D5C1] 
                            shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                {/* Product Image */}
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    data-testid={`product-image-${product.id}`}
                  />
                </div>
                
                {/* Product Info */}
                <div className="p-6 sm:p-8">
                  <div className="flex items-start justify-between mb-4">
                    <h3 
                      className="text-2xl sm:text-3xl font-semibold text-[#2C1E16]"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                      data-testid={`product-name-${product.id}`}
                    >
                      {product.name}
                    </h3>
                    <span 
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium 
                               uppercase tracking-widest bg-[#FDF6EC] text-[#E8651A] border border-[#E8651A]/30"
                      data-testid={`product-badge-${product.id}`}
                    >
                      Coming Soon
                    </span>
                  </div>
                  <p 
                    className="text-[#2C1E16]/70 leading-relaxed"
                    data-testid={`product-description-${product.id}`}
                  >
                    {product.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Why Amma Ruchi Section
const WhySection = () => {
  const features = [
    {
      icon: ChefHat,
      title: "Homemade Recipe",
      description: "Traditional recipes passed down through generations"
    },
    {
      icon: LeafyGreen,
      title: "No Preservatives",
      description: "Pure ingredients, naturally preserved the old way"
    },
    {
      icon: Sparkles,
      title: "Small Batch",
      description: "Made fresh in limited quantities for best quality"
    },
    {
      icon: Heart,
      title: "Made with Love",
      description: "Every jar is packed with care and warmth"
    }
  ];
  
  return (
    <section data-testid="why-section" className="py-24 sm:py-32 lg:py-40 bg-[#FDF6EC]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <AnimatedSection className="text-center mb-16">
          <h2 
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2C1E16] mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="why-heading"
          >
            Why Amma Ruchi?
          </h2>
        </AnimatedSection>
        
        <motion.div 
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="text-center group"
              data-testid={`why-tile-${index}`}
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl 
                            bg-[#2D6A2D]/10 mb-6 group-hover:bg-[#2D6A2D]/20 transition-colors">
                <feature.icon className="text-[#2D6A2D]" size={36} strokeWidth={1.5} />
              </div>
              <h3 
                className="text-xl sm:text-2xl font-semibold text-[#2C1E16] mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {feature.title}
              </h3>
              <p className="text-[#2C1E16]/70">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// How to Enjoy Section
const HowToEnjoySection = () => {
  const useCases = [
    {
      title: "Rice + Ghee",
      description: "Mix with hot rice and a spoonful of pure ghee",
      icon: "🍚"
    },
    {
      title: "Dosa",
      description: "Sprinkle on crispy dosas for extra flavor",
      icon: "🥞"
    },
    {
      title: "Roti",
      description: "Roll up with warm rotis or chapatis",
      icon: "🫓"
    },
    {
      title: "Curd Rice",
      description: "The perfect companion for comforting curd rice",
      icon: "🥣"
    }
  ];
  
  return (
    <section data-testid="how-to-enjoy-section" className="py-24 sm:py-32 lg:py-40 bg-[#F5EBDB]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <AnimatedSection className="text-center mb-16">
          <h2 
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2C1E16] mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="how-to-enjoy-heading"
          >
            How to Enjoy
          </h2>
          <p className="text-lg text-[#2C1E16]/70 max-w-xl mx-auto">
            Simple ways to bring Amma's flavors to your everyday meals
          </p>
        </AnimatedSection>
        
        <motion.div 
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {useCases.map((useCase, index) => (
            <motion.div
              key={useCase.title}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="bg-[#FDF6EC] rounded-2xl p-6 sm:p-8 border border-[#E5D5C1] text-center
                        hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              data-testid={`enjoy-tile-${index}`}
            >
              <div className="text-5xl mb-4">{useCase.icon}</div>
              <h3 
                className="text-xl sm:text-2xl font-semibold text-[#2C1E16] mb-2"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {useCase.title}
              </h3>
              <p className="text-sm text-[#2C1E16]/70">
                {useCase.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => {
  return (
    <footer id="contact" data-testid="footer-section" className="py-16 sm:py-20 bg-[#2C1E16]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 text-center">
        {/* Logo */}
        <img 
          src={LOGO_URL} 
          alt="Amma Ruchi" 
          className="h-20 mx-auto mb-8 rounded-2xl"
          data-testid="footer-logo"
        />
        
        {/* Message */}
        <p 
          className="text-2xl sm:text-3xl text-white/90 mb-6"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
          data-testid="footer-message"
        >
          Orders opening soon. Stay tuned.
        </p>
        
        {/* Instagram Link */}
        <a 
          href={INSTAGRAM_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-white/70 hover:text-[#E8651A] transition-colors mb-10"
          data-testid="footer-instagram-link"
        >
          <Instagram size={24} />
          <span className="text-lg">Follow us on Instagram</span>
        </a>
        
        {/* Divider */}
        <div className="h-px bg-white/10 mb-8" />
        
        {/* Made with love */}
        <p 
          className="text-white/50 flex items-center justify-center gap-2"
          data-testid="footer-location"
        >
          Made with <Heart size={16} className="text-[#E8651A]" fill="#E8651A" /> in Bengaluru
        </p>
      </div>
    </footer>
  );
};

// Texture Overlay Component
const TextureOverlay = () => {
  return (
    <div 
      className="fixed inset-0 pointer-events-none z-[100]"
      style={{
        backgroundImage: "url('https://images.pexels.com/photos/5993561/pexels-photo-5993561.jpeg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        mixBlendMode: "multiply",
        opacity: 0.08
      }}
      data-testid="texture-overlay"
    />
  );
};

// Main App Component
function App() {
  return (
    <div className="App bg-[#FDF6EC]" data-testid="app-container">
      <TextureOverlay />
      <Navbar />
      <main>
        <HeroSection />
        <StorySection />
        <ProductsSection />
        <WhySection />
        <HowToEnjoySection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
